/**
 * `goal` 契约 —— **I 域储蓄目标**（`DR-050` · 批 85）
 *
 * 依据：`I域PRD` **§5.1**（目标字段 / bucket 虚拟子罐 / 进度与节奏）· **§5.2.2**（手动攒 / 取出）·
 * **§5.3**（里程碑 / 贡献流）· `AC-I1.1`–`I1.8` · `AC-I2.1`–`I2.5` · `AC-I3.1`–`I3.7` ·
 * `AC-I4.1`–`I4.6` · `DR-050` D1–D9 · `DR-022` v1.1（超额不截断）· `DR-023`（端侧金额聚合 = 0）
 *
 * ## 🔴 三条"契约层就该堵死"的东西
 *
 * 1. **金额一律整数分**（`AC-I4.1`）—— 目标额 / 攒取金额都 `int()`，不接元文本；
 * 2. **`dueDate` 可空但不可空串** —— PRD `§5.1.1` 说「截止日可留空」，那是 `null`，
 *    不是 `''`（空串会让"有截止日"的判定在端侧和服务端各算一次，两边还容易不一致）；
 * 3. 🔴 **进度 / 节奏 / 还差多少 / 里程碑全部由服务端算好下发**（`DR-023` 反指标「端侧金额聚合 = 0」）
 *    ⇒ 端侧拿到的 `progressE4` / `remainingCents` / `pace` 是**结论**，不是原料。
 *
 * ## 🔴 攒/取请求只描述"动作 + 对手账户"，不描述腿
 *
 * 腿结构（谁 out、谁 in）在 `nucleus/goal.ts` 的 `goalLegsOf()` —— **唯一实现**。
 * 若让端侧发 `legs`，端侧就要知道"目标是 in 还是 out"，那是把口径搬到第二处（铁律 24）。
 */

import { z } from 'zod';
import { AutoSaveMode, GoalMove } from '../enums/plan.js';
import { GoalPolicy } from '../constants/index.js';
import type { Cents, RatioE4 } from '../types/brand.js';
import { DayString } from './pagination.js';

// ───────────────────────────── 视图 ─────────────────────────────

/** 里程碑格子（端侧只渲染 `reached`，**不自己算 `floor(pct/25)`**） */
export interface GoalMilestoneView {
  /** 档位（25 / 50 / 75 / 100） */
  tier: number;
  /** 是否已点亮 —— 🔴 由「**落库的最高档位**」决定，不由当前进度决定（取出不熄灭，`AC-I3.2`） */
  reached: boolean;
}

/** 卡面上的进度块 —— 全部由服务端算好（`DR-023`） */
export interface GoalProgressView {
  /** 进度（万分之一整数）：`86.0%` ⇒ `8600`。🔴 **不截断**，超额可 > 10000（`AC-I1.6`） */
  progressE4: RatioE4;
  /** 还差多少（分）—— 可**为负**（超额时） */
  remainingCents: Cents;
  /** 已达成 */
  reached: boolean;
  /** 超出部分（分，≥ 0） */
  overCents: Cents;
  /** 里程碑轨道（**全档位** + 每格是否点亮） */
  milestones: readonly GoalMilestoneView[];
  /** 历史最高档位（落库值，`0` = 一个都没点亮） */
  highestMilestone: number;
}

/** 节奏块 —— 无截止日时**整个块为 `null`**（`AC-I1.7`：不判节奏，连"预计达成日"都不给） */
export interface GoalPaceView {
  /** 判定结果（`reached` / `onTrack` / `behind` / `overdue` / `tooEarly`） */
  pace: 'reached' | 'onTrack' | 'behind' | 'overdue' | 'tooEarly';
  /** 观察到的整月数（不足 1 个月 ⇒ `null`，不编造） */
  monthsElapsed: number | null;
  /** 月均攒速（分/月，向下取整） */
  monthlyRateCents: Cents | null;
  /** 预计达成日（`YYYY-MM-DD`）；算不出 `null` */
  projectedDate: string | null;
  /** 距截止日天数（负数 = 已过期） */
  daysLeft: number | null;
  /** 一句话提示（建议句式，`AC-I4.3`） */
  hint: string;
}

/** 目标卡（列表与详情共用一面） */
export interface GoalView {
  id: string;
  name: string;
  /** 目标额（整数分） */
  targetCents: Cents;
  /** 截止日（可空 —— `AC-I1.2`） */
  dueDate: string | null;
  /** 关联账户（可空 —— 首次攒钱时绑定，`DR-050` D3） */
  accountId: string | null;
  accountName: string | null;
  icon: string | null;
  color: string | null;
  /** 🔴 币种 = **账本基础币种**（多币种目标属 P1，`I域PRD` §3.2） */
  currency: string;
  /** 🆕 自动攒模式（`DR-052` D4）；`off` = 不自动攒（升级后既有目标默认值） */
  autoSaveMode: AutoSaveMode;
  /** 🆕 自动攒额度：`fixed` ⇒ 分；`percent` ⇒ E4（10000 = 100%）；`off` ⇒ 0 */
  autoSaveValue: number;
  /** 已攒（分，**腿派生** —— `AC-I1.8`；表里没有这一列） */
  savedCents: Cents;
  /** 攒取流水笔数（0 ⇒ 可以换绑账户，>`0` ⇒ 拒绝） */
  moveCount: number;
  /** 贡献合计（**按成员稳定序**，🔴 绝不按金额排序 —— `AC-I3.4`） */
  contributions: readonly GoalContributionSummary[];
  progress: GoalProgressView;
  /** 🔴 无截止日 ⇒ `null`（不判节奏，`AC-I1.7`） */
  pace: GoalPaceView | null;
  /** 创建者 */
  ownerId: string;
  createdAt: number;
  updatedAt: number;
}

/** 某成员在该目标上的出资合计（**不是排行** —— `AC-I3.4`） */
export interface GoalContributionSummary {
  userId: string;
  /** 成员名（账本内昵称 / 账号名） */
  name: string;
  /** 净出资（分；取出会减少） */
  netCents: Cents;
}

/** 贡献流的一条（`AC-I3.3`：谁 / 何时 / 多少 / 备注 / 自动或手动） */
export interface GoalContributionView {
  transactionId: string;
  /** 动作（攒 / 取）—— 由**目标腿的方向**判定，不由端侧猜 */
  move: GoalMove;
  amountCents: Cents;
  date: string;
  note: string | null;
  /** 记账人 */
  userId: string;
  userName: string;
  /**
   * 是否自动攒。
   * 🔴 **本批恒 `false`**：自动攒依赖 F3 发薪日工作流（未实现，`DR-050` D8）。
   * 保留字段是为了让"手动/自动"这个展示位**现在就有确定语义**，而不是将来再加。
   */
  automatic: boolean;
}

/** `GET /api/goals` —— 🔴 **不分页**（目标是有界小集合，`AC-I1.3` 只要求 ≥5 个并存） */
export interface GoalListResult {
  items: readonly GoalView[];
  /** 账本基础币种（列表级，卡片不必重复携带语义） */
  currency: string;
  /** 合计（仅作总览；🔴 不是"预算"，不与 G 域混算） */
  totalTargetCents: Cents;
  totalSavedCents: Cents;
  /** 已达成的目标数（**不是比率**，避免端侧再除一次） */
  reachedCount: number;
}

/** `GET /api/goals/:id/contributions` —— 贡献流（时间倒序，**有界**） */
export interface GoalContributionListResult {
  goalId: string;
  currency: string;
  items: readonly GoalContributionView[];
  /** 超出上限被截断 ⇒ 明确上报（不静默） */
  truncated: boolean;
}

/**
 * `GET /api/goals/:id/contributions?limit=` —— 贡献流条数上限。
 *
 * 🔴 默认 50：目标攒钱是**长期累积**的，一次全下发会让响应随年数线性增长（`DR-006` 的性能纪律）。
 * 🔴 截断必须上报（`GoalContributionListResult.truncated`），不静默。
 */
export const GoalContributionQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type GoalContributionQuery = z.infer<typeof GoalContributionQuerySchema>;

/** 攒 / 取的结果 */
export interface GoalMoveResultView {
  goal: GoalView;
  /** 🔴 本次写入的流水 id（端侧可跳去流水页） */
  transactionId: string;
  /**
   * 本次动作后**是否新点亮了里程碑**。
   * 🔴 `false` 不代表"没变"，只代表"没有新档位" —— 回落时也是 `false`（`AC-I3.2` 只升不降）。
   */
  milestoneLit: boolean;
  /** 新点亮的档位（`null` = 没点亮新的） */
  litTier: number | null;
}

// ───────────────────────────── 请求 ─────────────────────────────

const GoalNameSchema = z
  .string()
  .trim()
  .min(1, '请填写目标名称')
  .max(GoalPolicy.NameMaxLength, `目标名称不超过 ${String(GoalPolicy.NameMaxLength)} 个字`);

/**
 * `POST /api/goals` —— 建目标（`AC-I1.1` / `AC-I1.2`）。
 *
 * 🔴 `accountId` **可省略**：目标可以先不绑账户，第一次「攒一笔」时再绑定（`DR-050` D3）。
 * 🔴 `dueDate` 用 `null` 表示"没有截止日"，**不接受空串**（见文件头第 2 条）。
 */
/** 🆕 自动攒模式（`DR-052` D4）—— 与 `enums/plan.ts` 的 `AutoSaveMode` 同值 */
export const AutoSaveModeSchema = z.enum([AutoSaveMode.Off, AutoSaveMode.Fixed, AutoSaveMode.Percent]);
/**
 * 🆕 自动攒额度（**非负整数**）。
 *
 * 🔴 量纲由 `autoSaveMode` 决定（`fixed` ⇒ 分、`percent` ⇒ E4），契约层**只做形态**：
 * 区间与"off 时必须为 0"由内核 `checkAutoSave()` 判（两处各写一份必然漂移）。
 */
export const AutoSaveValueSchema = z.number().int('额度必须是整数').nonnegative('额度不得为负');

export const CreateGoalRequestSchema = z.object({
  name: GoalNameSchema,
  /** 目标额（整数分、> 0） */
  target: z.number().int('目标金额必须是整数分').positive('目标金额必须大于 0'),
  dueDate: DayString.nullish(),
  accountId: z.string().trim().min(1).nullish(),
  icon: z.string().trim().max(40).nullish(),
  color: z.string().trim().max(20).nullish(),
  /**
   * 🆕 自动攒（`DR-052` D4）—— 不给 = `off`。
   * 🔴 它只描述"每期攒多少"，**不影响进度口径**（进度仍由腿派生，`DR-050` D2）。
   */
  autoSaveMode: AutoSaveModeSchema.optional(),
  autoSaveValue: AutoSaveValueSchema.optional(),
});
export type CreateGoalRequest = z.infer<typeof CreateGoalRequestSchema>;

/**
 * `PATCH /api/goals/:id` —— 改目标。
 *
 * 🔴 **不发的键 = 不改**（`DR-050` D3 的换绑规则在服务层判：**已有攒钱记录 ⇒ 换绑 400**）。
 * 🔴 `accountId: null` = 解绑（B 域的引用闸保证账户不会被"删掉"，只会被"隐藏"）。
 */
export const UpdateGoalRequestSchema = z.object({
  name: GoalNameSchema.optional(),
  target: z.number().int('目标金额必须是整数分').positive('目标金额必须大于 0').optional(),
  dueDate: DayString.nullable().optional(),
  accountId: z.string().trim().min(1).nullable().optional(),
  icon: z.string().trim().max(40).nullable().optional(),
  color: z.string().trim().max(20).nullable().optional(),
  /** 🆕 自动攒模式（不给 = 不改；改成 `off` 时必须同时把额度置 0，服务端会校验） */
  autoSaveMode: AutoSaveModeSchema.optional(),
  autoSaveValue: AutoSaveValueSchema.optional(),
});
export type UpdateGoalRequest = z.infer<typeof UpdateGoalRequestSchema>;

/** 攒 / 取动作取值（2 种） */
export const GoalMoveSchema = z.enum([GoalMove.Save, GoalMove.Withdraw]);

/**
 * `POST /api/goals/:id/move` —— 攒一笔 / 取出（`AC-I2.1`–`I2.5`）。
 *
 * | 字段 | `save` | `withdraw` |
 * |---|---|---|
 * | `counterAccountId` | **来源**账户 | **去向**账户 |
 * | `hostAccountId` | 仅在目标**尚未绑定账户**时必填（同批写回 `savings_goals.account_id`） | 忽略（未绑定 ⇒ 400） |
 *
 * 🔴 **不发 `legs`**：腿结构在 `nucleus/goal.ts`（唯一实现）。
 */
export const GoalMoveRequestSchema = z.object({
  move: GoalMoveSchema,
  /** 金额（整数分、> 0 —— 方向由 `move` 表达，不用负号） */
  amount: z.number().int('金额必须是整数分').positive('金额必须大于 0'),
  /** 动作日（账本本地日历日） */
  date: DayString,
  /** 对手账户：攒 = 来源；取 = 去向 */
  counterAccountId: z.string().trim().min(1, '请选择账户'),
  /** 目标宿主账户（仅"目标还没绑账户 + 攒钱"时需要） */
  hostAccountId: z.string().trim().min(1).nullish(),
  note: z.string().trim().max(GoalPolicy.NoteMaxLength).nullish(),
});
export type GoalMoveRequest = z.infer<typeof GoalMoveRequestSchema>;

/**
 * `DELETE /api/goals/:id` —— 删目标（`AC-I4.5` / E5）。
 *
 * 🔴 `forfeit` 的含义：**放弃归属**。已攒 > 0 时不带它就 400 ——
 * 删除会把流水的 `goal_id` 置空（腿一条不删），"这笔是给日本旅行攒的"这条信息**会丢**。
 * 必须让用户显式确认这件事，而不是点两下就没了。
 */
export const DeleteGoalQuerySchema = z.object({
  forfeit: z
    .enum(['true', 'false'])
    .default('false')
    // 🔴 用字面量 + transform，**不用 `z.coerce.boolean()`** —— 后者把 `'false'` 当 `true`
    //    （铁律：`56` 批实测踩过，`?includeEarlier=false` 静默变成 true）
    .transform((v) => v === 'true'),
});
export type DeleteGoalQuery = z.infer<typeof DeleteGoalQuerySchema>;

/** 删目标的返回值 */
export interface GoalDeletedView {
  id: string;
  /** 失去归属的流水笔数（腿一条不删 —— `AC-I4.5`） */
  detachedTransactionCount: number;
}

// ───────────────────────────── H 域取数面 ─────────────────────────────

/**
 * `GoalService.listReminderCandidates()` 的返回项（**只读面** · `AC-I4.2`）。
 *
 * 🔴 **I 域不生成提醒**：本结构只是"我这边有这些事值得提醒"，
 * 写 `reminder_instances` 的永远是 H 域（`AC-H1.13`）。这就是「I 算 H 拉」。
 */
export interface GoalReminderCandidate {
  goalId: string;
  goalName: string;
  /**
   * 提醒锚点（进入 H 域窗口键，最终形如 `goal:<goalId>:<档位>`）。
   *
   * 🔴 **必须带目标 id**：H 域的**展示合并键**是 `type|windowKey`、**不含 `entityId`**
   * ⇒ 只用档位做锚点，会让"两个目标同处 50%"被合并成一条、只剩第一个目标的名字。
   */
  anchor: string;
  /** 一条候选属于哪类事（H 域据此选变体 / 文案） */
  kind: 'milestone' | 'paceBehind';
  /** 该目标当前的已攒（分）—— H 域展示"还差多少"用 */
  savedCents: Cents;
  targetCents: Cents;
}
